import {
  automationTemplateIdSchema,
  type Automation,
  type AutomationId,
  type AutomationSettings,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

function checkIsKnownAutomationTemplate(automation: Automation): boolean {
  return automationTemplateIdSchema.safeParse(automation.template_id).success;
}

const automationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allAutomations: builder.query<
      Automation[],
      void,
      { data: { automations: Automation[] } }
    >({
      query: () => "/automations",
      transformResponse: (response) =>
        response.data.automations.filter(checkIsKnownAutomationTemplate),
      providesTags: (result) =>
        getTagsProvidedByList(TagType.Automation, result),
    }),
    automationById: builder.query<
      Automation | undefined,
      AutomationId,
      { data: { automation: Automation } }
    >({
      query: (id) => `/automations/${encodeURIComponent(id)}`,
      transformResponse: (response) => {
        const { automation } = response.data;
        return checkIsKnownAutomationTemplate(automation)
          ? automation
          : undefined;
      },
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.Automation, id),
    }),
    createAutomation: builder.mutation<
      Automation,
      {
        template_id: AutomationTemplateId;
        name: string;
        is_enabled?: boolean;
        settings: AutomationSettings;
        session_id?: string;
        /** The `create_automation` proposal this automation is created from; it takes the id the proposal minted. */
        proposal_id?: CopilotProposalId;
      },
      { data: { automation: Automation } }
    >({
      query: (body) => ({ method: "POST", url: "/automations", body }),
      transformResponse: (response) => response.data.automation,
      // The new automation's page opens on the response instead of a loader.
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            automationApi.util.upsertQueryData(
              "automationById",
              created.id,
              created,
            ),
          );
        } catch {
          // The caller reports the failure.
        }
      },
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Automation),
    }),
    updateAutomation: builder.mutation<
      Automation,
      {
        id: AutomationId;
        name?: string;
        is_enabled?: boolean;
        settings?: AutomationSettings;
        session_id?: string;
      },
      { data: { automation: Automation } }
    >({
      query: ({ id, ...body }) => ({
        method: "PATCH",
        url: `/automations/${encodeURIComponent(id)}`,
        body,
      }),
      transformResponse: (response) => response.data.automation,
      // The saved automation is in the cache before the caller continues, so
      // the form is clean as soon as the save returns, not once the refetch
      // lands.
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: saved } = await queryFulfilled;
          // Synchronous, unlike an upsert.
          dispatch(
            automationApi.util.updateQueryData(
              "automationById",
              id,
              () => saved,
            ),
          );
        } catch {
          // The caller reports the failure.
        }
      },
      invalidatesTags: (result, error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.Automation, id),
    }),
    deleteAutomation: builder.mutation<void, AutomationId>({
      query: (id) => ({
        method: "DELETE",
        url: `/automations/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.Automation, id),
    }),
  }),
});

export const {
  useAllAutomationsQuery,
  useAutomationByIdQuery,
  useLazyAutomationByIdQuery,
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
  useDeleteAutomationMutation,
} = automationApi;
